'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface Agent {
  id: string;
  name: string;
  description?: string;
  organizationId: string;
  pipelineId: string;
  agentType?: 'MCP' | 'WIDGET' | 'CHAT_APP';
  createdAt: string;
  updatedAt: string;
  configDoc: {
    name?: string;
    description?: string;
    systemPrompt?: string;
    temperature?: number;
    maxTokens?: number;
    authMethod?: 'PASSWORD' | 'SAML' | 'BOTH' | 'PUBLIC';
    samlConfig?: any;
    defaultLanguage?: string;
    allowPersonalLanguagePreference?: boolean;
    translations?: any;
    voiceInputEnabled?: boolean;
    voiceInputModel?: string;
    voiceEnabledPlatforms?: any[];
    fileUploadEnabled?: boolean;
    maxFileSize?: number;
    allowedFileTypes?: string[];
    maxFilesPerMessage?: number;
    feedbackEnabled?: boolean;
    hidePoweredBy?: boolean;
    wideLogo?: string;
    squareLogo?: string;
    wideLogoDark?: string;
    squareLogoDark?: string;
    webhooks?: any[];
    organizationId?: string;
    [key: string]: any;
  };
  enabledModels?: Array<{
    displayName: string;
    isEnabled: boolean;
    llmPlatformId: string;
    llmProvider: string;
    model: string;
    modelLabel?: string;
  }>;
}

interface UpdateAgentData {
  [key: string]: any;
}

export function useAgent(agentId: string, options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();

  const {
    data: agent,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<Agent>({
    queryKey: ['agent', agentId],
    queryFn: async () => {
      const response = await fetch(`/api/chat/agents/${agentId}`);
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Failed to load agent (${response.status})`);
      }
      
      return response.json();
    },
    // Only enable query if explicitly allowed (defaults to true for backward compatibility)
    enabled: options?.enabled !== false,
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Keep in cache for 10 minutes
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (error instanceof Error && error.message.includes('401')) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Mutation for updating agent settings
  const updateAgent = useMutation({
    mutationFn: async (data: UpdateAgentData) => {
      const response = await fetch(`/api/chat/agents/${agentId}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update agent');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate and refetch agent data
      queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    },
  });

  return {
    agent,
    isLoading,
    isError,
    error,
    refetch,
    updateAgent,
    // Convenience accessors
    name: agent?.name,
    description: agent?.description,
    configDoc: agent?.configDoc || {},
    enabledModels: agent?.enabledModels || [],
    authMethod: agent?.configDoc?.authMethod || 'PASSWORD',
    organizationId: agent?.organizationId || agent?.configDoc?.organizationId,
    agentType: agent?.agentType,
  };
}